// src/components/Costplanner/ProductGallery.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useFeedback } from "../../contexts/FeedbackContext";
import { usePlanner } from "../../contexts/PlannerContext";
import { useStorage } from "../../contexts/StorageContext";
import mudoLogo from "../../assets/mudo-logo-bg.png";

const LS_KEY = "mudoProducts";
const BATCHES_KEY = "mudoBatches";
const PRODUCTS_FILE = "mudo/products.json";
const BATCHES_FILE = "batches/mudoBatches.json";

/* ---------- Small modal ---------- */
function AddToBatchModal({ open, product, onClose, onCreate, isDark }) {
  const [batchName, setBatchName] = useState("");
  const [qty, setQty] = useState(12);

  useEffect(() => {
    if (!open || !product) return;
    const fallback = `${product?.name || "Batch"} — ${new Date().toLocaleDateString()}`;
    setBatchName(product?.batchTemplateName || fallback);
    setQty(Number(product?.defaultBatchQty || 12));
  }, [open, product]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,.45)", backdropFilter: "blur(2px)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl shadow-xl"
        style={{
          background: isDark ? "rgba(31,31,31,1)" : "#fff",
          color: isDark ? "#fff" : "#0b1220",
          border: isDark ? "1px solid rgba(255,255,255,.12)" : "1px solid rgba(0,0,0,.08)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="px-4 py-3 rounded-t-xl"
          style={{ borderBottom: isDark ? "1px solid rgba(255,255,255,.10)" : "1px solid rgba(0,0,0,.06)" }}
        >
          <div className="text-lg font-semibold">Add to Batch</div>
          <div className="text-sm opacity-70 truncate">{product?.name || "Unnamed Product"}</div>
        </div>

        <div className="px-4 py-4 space-y-3">
          <label className="block text-sm">
            <div className="mb-1 opacity-75">Batch name</div>
            <input
              value={batchName}
              onChange={(e) => setBatchName(e.target.value)}
              className="w-full px-3 py-2 rounded border"
              style={{
                background: isDark ? "rgba(0,0,0,.35)" : "#fff",
                borderColor: isDark ? "rgba(255,255,255,.18)" : "rgba(0,0,0,.15)",
                color: isDark ? "#fff" : "#0b1220",
              }}
              placeholder="e.g., Espresso Mugs • Feb 10"
            />
          </label>

          <label className="block text-sm">
            <div className="mb-1 opacity-75">Quantity</div>
            <input
              type="number"
              min={1}
              value={qty}
              onChange={(e) => setQty(Math.max(1, Number(e.target.value || 1)))}
              className="w-32 px-3 py-2 rounded border"
              style={{
                background: isDark ? "rgba(0,0,0,.35)" : "#fff",
                borderColor: isDark ? "rgba(255,255,255,.18)" : "rgba(0,0,0,.15)",
                color: isDark ? "#fff" : "#0b1220",
              }}
            />
          </label>

          <div className="text-xs opacity-70">
            A sequence of stage tasks will be created and sent to the Planner.
          </div>
        </div>

        <div
          className="px-4 py-3 flex items-center justify-end gap-2 rounded-b-xl"
          style={{ borderTop: isDark ? "1px solid rgba(255,255,255,.10)" : "1px solid rgba(0,0,0,.06)" }}
        >
          <button onClick={onClose} className="px-3 py-2 rounded text-sm" style={{ background: isDark ? "rgba(255,255,255,.10)" : "rgba(0,0,0,.06)" }}>
            Cancel
          </button>
          <button
            onClick={() => onCreate({ batchName, qty })}
            className="px-3 py-2 rounded text-sm text-white"
            style={{ background: "var(--color-primary, #f97316)" }}
          >
            Create & Add Tasks
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Task builder ---------- */
function startOfDay(d = new Date()) { const x = new Date(d); x.setHours(9,0,0,0); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate()+n); return x; }
function toIso(dt) { try { return dt.toISOString(); } catch { return new Date().toISOString(); } }

const DEFAULT_STAGES = [
  { key: "forming",     label: "Forming",      days: 1, color: "#06b6d4" },
  { key: "drying",      label: "Drying",       days: 2, color: "#22c55e" },
  { key: "bisque",      label: "Bisque",       days: 1, color: "#f59e0b" },
  { key: "glazing",     label: "Glazing",      days: 1, color: "#a78bfa" },
  { key: "glazeFiring", label: "Glaze Firing", days: 1, color: "#ef4444" },
  { key: "qcPack",      label: "QC & Pack",    days: 1, color: "#14b8a6" },
  { key: "dispatch",    label: "Dispatch",     days: 1, color: "#64748b" },
];

function makePlannerTasksFromProduct({ product, batchName, qty, startDate }) {
  const base = startOfDay(startDate || new Date());
  const tasks = [];
  let cursor = base;
  let prevId = null;

  DEFAULT_STAGES.forEach((stg) => {
    const start = cursor;
    const end = addDays(start, Math.max(1, stg.days) - 1);

    const id = (crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    const task = {
      id,
      title: `${batchName} — ${stg.label}`,
      start: toIso(start),
      end:   toIso(new Date(end.getFullYear(), end.getMonth(), end.getDate(), 17, 0, 0, 0)),
      qty,
      batch: batchName,
      category: product?.category || "Batch",
      color: stg.color,
      tags: [stg.key],
      status: "todo",
      progress: 0,
      predecessorType: "FS",
      dependencies: prevId ? [String(prevId)] : [],
      notes: `Auto-created from product “${product?.name || "Unnamed"}”.`,
    };

    tasks.push(task);
    cursor = addDays(end, 1);
    prevId = id;
  });

  return tasks;
}

/* ---------- Main ---------- */
export default function ProductGallery({
  settings,
  products = [],
  onBack,
  onOpenPlanner,
  onCreateNew,            // parent’s create handler (we’ll gate it)
  onAddTasksToPlanner,
  onProductsChange,
  currentPlan = "free",   // NEW: "free" | "studio" | "pro"
  onRequireUpgrade,       // NEW: open subscription modal
  isFreeOverride = null,  // allow parent to force free/paid gating (e.g., based on entitlements)
}) {
  const { play, buzz } = useFeedback();
  const planner = (() => { try { return usePlanner(); } catch { return null; } })();
  const { hasFolder, loadJSON, saveJSON, workspaceName, mode } = useStorage();

  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("updated");
  const [list, setList] = useState(products);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalProduct, setModalProduct] = useState(null);

  // ---- Free-plan limit logic
  const isFree = typeof isFreeOverride === "boolean"
    ? isFreeOverride
    : (currentPlan || "free").toLowerCase() === "free";
  const FREE_LIMIT = 3;
  const limitReached = isFree && (list?.length || 0) >= FREE_LIMIT;

  const seededRef = useRef(false);
  useEffect(() => setList(products), [products]);

  // Workspace change key
  const workspaceKey = `${mode || "none"}:${workspaceName || ""}`;

  // Seed from folder when workspace changes
  useEffect(() => {
    let alive = true;
    (async () => {
      seededRef.current = false;

      if (!hasFolder) return;

      const disk = await loadJSON(PRODUCTS_FILE, null);
      if (!alive) return;

      if (Array.isArray(disk)) {
        try { localStorage.setItem(LS_KEY, JSON.stringify(disk)); } catch {}
        setList(disk);
        onProductsChange?.(disk);
      } else {
        try { localStorage.removeItem(LS_KEY); } catch {}
        setList([]);
        onProductsChange?.([]);
      }

      seededRef.current = true;
    })();

    return () => { alive = false; };
  }, [hasFolder, workspaceKey, loadJSON, onProductsChange]);

  // Legacy boot from LS only when no folder is linked
  useEffect(() => {
    if (hasFolder || seededRef.current) return;
    if (products?.length) return;
    try {
      const fromLS = JSON.parse(localStorage.getItem(LS_KEY) || "[]");
      if (Array.isArray(fromLS)) setList(fromLS);
    } catch {}
  }, [products, hasFolder]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = (list || []).filter((p) => {
      if (!q) return true;
      return (
        (p.name || "").toLowerCase().includes(q) ||
        (p.category || "").toLowerCase().includes(q) ||
        (p.method || "").toLowerCase().includes(q)
      );
    });
    if (sortBy === "name") out.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    else {
      out.sort(
        (a, b) =>
          (Number(b.updatedAt) || Number(b.savedAt) || 0) -
          (Number(a.updatedAt) || Number(a.savedAt) || 0)
      );
    }
    return out;
  }, [list, query, sortBy]);

  async function mirrorToFolder(next) {
    if (!hasFolder) return;
    try { await saveJSON(PRODUCTS_FILE, next); } catch {}
  }
  function persist(next) {
    try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch {}
    setList(next);
    onProductsChange?.(next);
    mirrorToFolder(next);
  }

  function handleDelete(prod) {
    play("tap");
    const ok = window.confirm(`Delete “${prod.name || "Unnamed Product"}”? This cannot be undone.`);
    if (!ok) { buzz("light"); return; }
    const next = (list || []).filter((p) => String(p.id) !== String(prod.id));
    persist(next);
    play("stop"); buzz("medium");
  }

  function handleEdit(prod) {
    const next = (list || []).map((p) =>
      String(p.id) === String(prod.id) ? { ...p, _lastOpenedAt: Date.now() } : p
    );
    persist(next);
    play("tap"); buzz("light");
    onOpenPlanner?.(prod);
  }

  function openAddToBatch(prod) { setModalProduct(prod); setModalOpen(true); play("tap"); }

  async function persistBatchFolderAware(next) {
    try { localStorage.setItem(BATCHES_KEY, JSON.stringify(next)); } catch {}
    if (hasFolder) { try { await saveJSON(BATCHES_FILE, next); } catch {} }
  }

  function handleCreateBatch({ batchName, qty }) {
    if (!modalProduct) return;

    let existing = [];
    try { existing = JSON.parse(localStorage.getItem(BATCHES_KEY) || "[]"); } catch {}

    const newBatch = {
      id: (crypto?.randomUUID?.() ?? `b-${Date.now()}`),
      name: batchName,
      qty,
      productId: modalProduct?.id ?? null,
      productName: modalProduct?.name ?? "",
      createdAt: Date.now(),
      status: "Not Started",
      products: modalProduct?.id ? [modalProduct.id] : [],
    };
    const nextBatches = [newBatch, ...existing];
    persistBatchFolderAware(nextBatches);

    const tasks = makePlannerTasksFromProduct({
      product: modalProduct,
      batchName: newBatch.name,
      qty: newBatch.qty,
      startDate: new Date(),
    });

    onAddTasksToPlanner?.(tasks);
    try { planner?.upsertEvents?.(tasks); } catch {}

    setModalOpen(false);
    setModalProduct(null);
    play("success"); buzz("success");
  }

  // NEW: guarded New Product handler
  function handleNewProductClick() {
    if (limitReached) {
      try {
        window.dispatchEvent?.(
          new CustomEvent("mudo:toast", {
            detail: {
              type: "info",
              message: `Free plan limit reached (${FREE_LIMIT} products). Upgrade to add more.`,
            },
          })
        );
      } catch {}
      buzz("light");
      onRequireUpgrade?.();
      return;
    }
    play("start"); buzz("light");
    onCreateNew?.();
  }

  const isDark = !!settings?.isDarkMode;

  return (
    <div className={`min-h-screen p-6 ${isDark ? "bg-neutral-900 text-white" : "bg-neutral-100 text-neutral-900"}`} style={{ transition: "background-color 200ms, color 200ms" }}>
      {/* Header */}
      <div
        className="mb-5 p-4 rounded-lg flex items-center justify-between shadow"
        style={{
          background: isDark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.65)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          border: isDark ? "1px solid rgba(255,255,255,0.18)" : "1px solid rgba(0,0,0,0.08)",
        }}
      >
        <div className="flex items-center gap-3">
          <img src={mudoLogo} alt="Mudo" className="h-10 w-10" />
          <div>
            <h1 className="text-xl font-semibold">Product Gallery</h1>
            <p className="text-xs opacity-70">
              {filtered.length} item{filtered.length !== 1 ? "s" : ""} •{" "}
              {isFree ? `Free plan limit: ${FREE_LIMIT}` : `Plan: ${String(currentPlan).toUpperCase()}`}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { play("tap"); buzz("light"); onBack?.(); }}
            className="px-3 py-2 rounded text-sm bg-zinc-700 text-white hover:opacity-90"
          >
            ← Back
          </button>
          <button
            onClick={handleNewProductClick}
            disabled={limitReached}
            className={`px-3 py-2 rounded text-sm text-white hover:opacity-95 ${
              limitReached ? "opacity-60 cursor-not-allowed bg-neutral-600" : ""
            }`}
            style={{ background: "var(--color-primary)" }}
            title={limitReached ? `Upgrade to add more than ${FREE_LIMIT}` : "Create a new product"}
          >
            + New Product
          </button>
        </div>
      </div>

      {/* Limit banner */}
      {limitReached && (
        <div className="mb-4 rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3 text-sm flex items-start justify-between gap-3">
          <div>
            <div className="font-semibold text-yellow-300">Limit reached</div>
            <div className="text-yellow-200/85">
              You’ve reached the Free plan limit of {FREE_LIMIT} products. Upgrade to add more.
            </div>
          </div>
          <button
            onClick={() => onRequireUpgrade?.()}
            className="shrink-0 px-3 py-1.5 rounded bg-orange-500 hover:bg-orange-600 text-white font-semibold"
          >
            Upgrade
          </button>
        </div>
      )}

      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && play("tap")}
          placeholder="Search name, category, method…"
          className={`px-3 py-2 rounded border w-full sm:w-80 ${isDark ? "bg-zinc-900 border-zinc-700 text-white" : "bg-white border-zinc-300"}`}
        />
        <select
          value={sortBy}
          onChange={(e) => { setSortBy(e.target.value); play("tap"); buzz("light"); }}
          className={`px-3 py-2 rounded border ${isDark ? "bg-zinc-900 border-zinc-700 text-white" : "bg-white border-zinc-300"}`}
        >
          <option value="updated">Sort: Last updated</option>
          <option value="name">Sort: Name A→Z</option>
        </select>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="opacity-70 text-sm">No products yet. Create your first one!</div>
      ) : (
        <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(230px,1fr))]">
          {filtered.map((p) => {
            const img = p.imageUrl || p.photoUrl || p.image || (Array.isArray(p.images) ? p.images[0] : "");
            const updated = p.updatedAt || p.savedAt || p._lastOpenedAt || p.createdAt || Date.now();
            const subtitle = [p.category, p.method].filter(Boolean).join(" • ");
            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="rounded-lg overflow-hidden shadow-sm hover:shadow-md"
                style={{
                  background: isDark ? "rgba(255,255,255,0.04)" : "white",
                  border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.06)",
                }}
              >
                <div className="h-36 bg-zinc-200 dark:bg-zinc-800 relative">
                  {img ? (
                    <img
                      src={img}
                      alt={p.name || "Product"}
                      className="absolute inset-0 h-full w-full object-cover"
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  ) : null}
                </div>
                <div className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{p.name || "Unnamed Product"}</div>
                      <div className="text-xs opacity-70 truncate">{subtitle || "—"}</div>
                    </div>
                    <div className="text-[10px] opacity-60 whitespace-nowrap">
                      {new Date(updated).toLocaleDateString()}
                    </div>
                  </div>

                  <div className="mt-3 grid grid-cols-3 gap-2">
                    <button
                      onClick={() => openAddToBatch(p)}
                      className="px-2 py-1 rounded text-sm text-white hover:opacity-95"
                      style={{ background: "var(--color-primary, #f97316)" }}
                      title="Create a batch and add all stage tasks to the planner"
                    >
                      Add to Batch
                    </button>
                    <button
                      onClick={() => handleEdit(p)}
                      className="px-2 py-1 rounded text-sm text-white hover:opacity-95"
                      style={{ background: "var(--color-secondary, #6366f1)" }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(p)}
                      className="px-2 py-1 rounded text-sm bg-red-600 text-white hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Add-to-batch modal */}
      <AddToBatchModal
        open={modalOpen}
        product={modalProduct}
        isDark={isDark}
        onClose={() => { setModalOpen(false); setModalProduct(null); buzz("light"); }}
        onCreate={handleCreateBatch}
      />
    </div>
  );
}
